import { DialogComponent } from '@theme/dialog';
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const FEEDBACK_DURATION = 2500;

class VideoGridComponent extends DialogComponent {
  #product = null;
  #variant = null;
  #current = null;
  #busy = false;
  #resetTimer = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('dialog:close', this.#teardown);
    this.addEventListener('keydown', this.#onKeydown);

    const label = this.dataset.textCardAdd;
    if (label) {
      for (const element of this.querySelectorAll('.video-card__button-label')) element.textContent = label;
    }

    for (const card of this.#cards()) this.#syncCard(card);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('dialog:close', this.#teardown);
    this.removeEventListener('keydown', this.#onKeydown);
    clearTimeout(this.#resetTimer);
  }

  openVideo(event) {
    this.#open(event.target.closest('[data-video-card]'));
  }

  previous() {
    this.#step(-1);
  }

  next() {
    this.#step(1);
  }

  quickAdd(event) {
    const card = event.target.closest('[data-video-card]');
    const data = this.#readCard(card);
    if (!data?.product) return;

    const variant = this.#cardVariant(card, data.product);

    if (variant) {
      const button = event.target.closest('.video-card__button');
      const label = button?.querySelector('.video-card__button-label');
      if (variant.available && button && label) this.#add(variant, data.product, label, button, false);
      return;
    }

    if (data.product.variants.length > 1) this.#open(card);
  }

  changeCardVariant(event) {
    this.#syncCard(event.target.closest('[data-video-card]'));
  }

  changeVariant(event) {
    const variant = this.#product?.variants.find((item) => String(item.id) === event.target.value);
    if (variant) this.#setVariant(variant);
  }

  addToCart() {
    const { addLabel, addButton } = this.refs;
    if (!this.#variant?.available || !this.#product) return;
    this.#add(this.#variant, this.#product, addLabel, addButton, true);
  }

  #cardVariant(card, product) {
    const { variants } = product;
    const select = this.dataset.showCardVariants === 'false' ? null : card.querySelector('.video-card__select');

    if (select) return variants.find((variant) => String(variant.id) === select.value) ?? null;
    if (variants.length === 1) return variants[0];
    return null;
  }

  #syncCard(card) {
    const data = this.#readCard(card);
    const button = card?.querySelector('.video-card__button');
    const label = button?.querySelector('.video-card__button-label');
    if (!data?.product || !button || !label) return;

    const variant = this.#cardVariant(card, data.product);
    const soldOut = variant
      ? !variant.available
      : !data.product.variants.some((item) => item.available);

    label.textContent = soldOut ? this.dataset.textSoldOut : this.dataset.textCardAdd;

    if (soldOut) {
      button.setAttribute('aria-disabled', 'true');
    } else {
      button.removeAttribute('aria-disabled');
    }
  }

  #cards() {
    return [...this.querySelectorAll('[data-video-card]')];
  }

  #readCard(card) {
    const script = card?.querySelector('script[data-video-data]');
    if (!script) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  #step(direction) {
    const cards = this.#cards();
    if (cards.length < 2 || !this.#current) return;

    let index = cards.indexOf(this.#current) + direction;

    if (index < 0 || index >= cards.length) {
      if (this.refs.dialog.dataset.loop === 'false') return;
      index = (index + cards.length) % cards.length;
    }

    this.#open(cards[index]);
  }

  #onKeydown = (event) => {
    if (!this.refs.dialog?.open) return;
    if (event.target instanceof HTMLSelectElement || event.target instanceof HTMLInputElement) return;

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.#step(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.#step(1);
    }
  };

  #open(card) {
    const data = this.#readCard(card);
    if (!data) return;

    const { dialog, frame, info, videoTitle, videoDescription, productBlock } = this.refs;

    this.#current = card;
    clearTimeout(this.#resetTimer);
    this.#busy = false;

    const showInfo = dialog.dataset.showInfo !== 'false' && Boolean(data.title || data.description);
    const showProduct = dialog.dataset.showProduct !== 'false' && Boolean(data.product);

    videoTitle.textContent = data.title || '';
    videoDescription.textContent = data.description || '';
    info.hidden = !showInfo;
    productBlock.hidden = !showProduct;
    dialog.dataset.hasAside = String(showInfo || showProduct);

    if (showProduct) {
      this.#renderProduct(data.product);
    } else {
      this.#product = null;
      this.#variant = null;
    }

    frame.replaceChildren(this.#buildPlayer(data));
    this.#updateNavigation();

    if (!dialog.open) this.showDialog();
  }

  #updateNavigation() {
    const { dialog } = this.refs;
    const cards = this.#cards();
    const index = cards.indexOf(this.#current);
    const loop = dialog.dataset.loop !== 'false';

    dialog.dataset.hasNav = String(cards.length > 1);

    for (const button of this.querySelectorAll('.video-grid__nav--prev')) button.disabled = !loop && index <= 0;
    for (const button of this.querySelectorAll('.video-grid__nav--next')) {
      button.disabled = !loop && index >= cards.length - 1;
    }
  }

  #buildPlayer({ id, title }) {
    const { dialog } = this.refs;
    const params = new URLSearchParams({ rel: '0', playsinline: '1', modestbranding: '1' });

    if (dialog.dataset.autoplay !== 'false') params.set('autoplay', '1');
    if (dialog.dataset.muted === 'true') params.set('mute', '1');

    const iframe = document.createElement('iframe');
    iframe.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?${params}`;
    iframe.title = title || 'YouTube';
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
    iframe.allowFullscreen = true;

    return iframe;
  }

  #teardown = () => {
    this.refs.frame?.replaceChildren();
    this.#current = null;
  };

  #renderProduct(product) {
    const { dialog, productMedia, productImage, productTitle, variantField, variantSelect, viewLink } = this.refs;

    this.#product = product;
    productTitle.textContent = product.title;
    productTitle.href = product.url;
    productMedia.href = product.url;
    viewLink.href = product.url;

    if (product.image) {
      productImage.src = product.image;
      productImage.alt = product.title;
      productMedia.hidden = false;
    } else {
      productMedia.hidden = true;
    }

    variantSelect.replaceChildren(
      ...product.variants.map((variant) => {
        const option = document.createElement('option');
        option.value = String(variant.id);
        option.textContent = variant.available ? variant.title : `${variant.title} — ${this.dataset.textSoldOut}`;
        option.disabled = !variant.available;
        return option;
      })
    );

    variantField.hidden = !(product.variants.length > 1 && dialog.dataset.showVariants !== 'false');

    const first = product.variants.find((variant) => variant.available) ?? product.variants[0];
    variantSelect.value = String(first.id);
    this.#setVariant(first);
  }

  #setVariant(variant) {
    const { productPrice, productCompare, addButton, addLabel, message } = this.refs;

    this.#variant = variant;
    productPrice.textContent = variant.price;
    productCompare.textContent = variant.compare;
    message.textContent = '';

    if (variant.available) {
      addButton.removeAttribute('aria-disabled');
      addLabel.textContent = addLabel.dataset.textAdd;
    } else {
      addButton.setAttribute('aria-disabled', 'true');
      addLabel.textContent = this.dataset.textSoldOut;
    }
  }

  async #refreshCart() {
    const cartItems = document.querySelector('cart-items-component');

    if (cartItems) {
      await customElements.whenDefined('cart-items-component');
      return cartItems.fetchCartData();
    }

    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });

    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  async #add(variant, product, label, button, fromDialog) {
    if (this.#busy) return;
    this.#busy = true;

    clearTimeout(this.#resetTimer);
    button.setAttribute('aria-disabled', 'true');

    const original = label.dataset.textAdd || this.dataset.textCardAdd;
    const formData = new FormData();
    formData.set('id', String(variant.id));
    formData.set('quantity', '1');

    const sections = [...document.querySelectorAll('cart-items-component')]
      .map((element) => element.dataset.sectionId)
      .filter(Boolean);
    if (sections.length) formData.set('sections', sections.join(','));

    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variant.id), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    const detail = {
      source: 'video-grid-component',
      sourceId: this.id || 'video-grid',
      itemCount: 1,
      productId: String(product.id),
    };

    try {
      const config = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: { ...config.headers, Accept: 'text/html' },
      }).then((result) => result.json());

      const cart = await this.#refreshCart();
      const failed = Boolean(response.status);

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { ...detail, items: cart.items, sections: response.sections, didError: failed },
      });

      if (failed) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: response.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: response.description, errors: response.errors },
          })
        );
        this.#feedback(label, button, original, response.description || response.message || this.dataset.textError, fromDialog);
      } else {
        this.#feedback(label, button, original, this.dataset.textAdded, fromDialog);
        if (fromDialog && this.refs.dialog.dataset.closeOnAdd === 'true') {
          setTimeout(() => this.closeDialog(), 450);
        }
      }
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      this.#feedback(label, button, original, this.dataset.textError, fromDialog);
    } finally {
      this.#busy = false;
    }
  }

  #feedback(label, button, original, text, fromDialog) {
    const { message } = this.refs;

    if (fromDialog) {
      message.textContent = text;
    } else {
      label.textContent = text;
    }

    this.#resetTimer = setTimeout(() => {
      if (fromDialog) {
        message.textContent = '';
      } else {
        label.textContent = original;
      }

      if (!fromDialog || this.#variant?.available) button.removeAttribute('aria-disabled');
    }, FEEDBACK_DURATION);
  }
}

if (!customElements.get('video-grid-component')) {
  customElements.define('video-grid-component', VideoGridComponent);
}
